// src/pages/ProductsPage.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/formatters';
import { productService } from '../services/productService';
import imageCompression from 'browser-image-compression';
import { 
    XCircleIcon, PlusCircleIcon, TrashIcon, DocumentTextIcon, 
    PhotoIcon, PencilSquareIcon, DocumentArrowDownIcon, DocumentArrowUpIcon,
    CubeIcon, Square3Stack3DIcon, ArchiveBoxIcon
} from '@heroicons/react/24/solid';

const getDirectUrl = (url) => {
    if (!url) return '';
    if (!url.includes('drive.google.com')) return url;
    try {
        let id = '';
        const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
        if (fileIdMatch && fileIdMatch[1]) id = fileIdMatch[1];
        else {
            const idParamMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
            if (idParamMatch && idParamMatch[1]) id = idParamMatch[1];
        }
        if (id) return `https://drive.google.com/thumbnail?id=${id}&sz=w1000`;
        return url; 
    } catch (e) { return url; }
};

const QuickAddSimpleModal = ({ initialName, onClose, onSave, setNotification, systemParameters }) => {
    const [name, setName] = useState(initialName);
    const [unit, setUnit] = useState(systemParameters?.unidades?.[0] || 'unid');
    const [cost, setCost] = useState(0);
    const [stock, setStock] = useState(0); 
    const [loading, setLoading] = useState(false);

    const handleSave = async () => {
        if (!name.trim() || cost < 0) {
            setNotification({ message: 'Nombre y costo válido son requeridos.', type: 'error' });
            return;
        }
        setLoading(true);
        const productData = {
            name: name.trim(),
            description: '',
            type: 'simple',
            unit: unit,
            cost: parseFloat(cost) || 0,
            stock: parseInt(stock) || 0,
        };
        try {
            const savedProduct = await productService.saveProduct({ productData, isEditMode: false });
            onSave(savedProduct);
            setNotification({ message: `Producto "${name}" creado.`, type: 'success' });
        } catch (error) {
            setNotification({ message: `Error: ${error.message}`, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
         <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md transition-colors">
                <h3 className="text-lg font-semibold mb-4 dark:text-gray-100 transition-colors">Crear Nuevo Producto Simple</h3>
                 <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Nombre *</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors" required />
                    </div>
                     <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Unidad</label>
                            <select value={unit} onChange={e => setUnit(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors">
                                {systemParameters?.unidades?.map(u => <option key={u} value={u}>{u}</option>) ?? <option value="unid">unid</option>}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Costo Unitario *</label>
                            <input type="number" value={cost} onChange={e => setCost(parseFloat(e.target.value) || 0)} min="0" step="any" className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors" required/>
                        </div>
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Stock Inicial</label>
                            <input type="number" value={stock} onChange={e => setStock(parseInt(e.target.value) || 0)} min="0" className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors"/>
                        </div>
                    </div>
                 </div>
                 <div className="flex justify-end space-x-3 pt-5 mt-4 border-t dark:border-gray-700 transition-colors">
                    <button type="button" onClick={onClose} className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg text-sm transition-colors">Cancelar</button>
                    <button type="button" onClick={handleSave} disabled={loading} className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:bg-blue-300 dark:disabled:bg-blue-900/50 text-sm transition-colors">
                        {loading ? 'Creando...' : 'Crear y Añadir'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProductFormModal = ({ product, isKit, onClose, onSave, allProducts, setNotification, systemParameters, updateAllProducts }) => { 
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    const availableUnits = useMemo(() => {
        return systemParameters?.unidades || ['unid'];
    }, [systemParameters]);

    const [unit, setUnit] = useState(availableUnits[0]);
    const [cost, setCost] = useState(0);
    const [stock, setStock] = useState(0);
    const [imageUrl, setImageUrl] = useState(''); 
    const [pdfUrl, setPdfUrl] = useState('');
    
    const [imageFile, setImageFile] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const [components, setComponents] = useState([]);
    const [componentSearch, setComponentSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedComponent, setSelectedComponent] = useState(null);
    const [componentQuantity, setComponentQuantity] = useState(1);
    const [showQuickAddModal, setShowQuickAddModal] = useState(false);
    const [kitUnit, setKitUnit] = useState('Kit');

    useEffect(() => {
        if (product) {
            setName(product.name || '');
            setDescription(product.description || '');
            if (isKit) {
                const initialComponents = Array.isArray(product.components) ? product.components : [];
                setComponents(initialComponents);
                setKitUnit(availableUnits.includes(product.unit) ? product.unit : 'Kit');
            } else {
                setUnit(availableUnits.includes(product.unit) ? product.unit : availableUnits[0]);
                setCost(product.cost || 0);
                setStock(product.stock || 0); 
                setImageUrl(product.imageUrl || '');
                setImagePreview(product.imageUrl ? getDirectUrl(product.imageUrl) : null);
                setPdfUrl(product.pdfUrl || '');
            }
        } else {
            setName('');
            setDescription('');
            setUnit(availableUnits[0]);
            setCost(0);
            setStock(0);
            setImageUrl(''); 
            setPdfUrl(''); 
            setComponents([]);
            setKitUnit('Kit');
        }
    }, [product, isKit, availableUnits]);

    useEffect(() => {
        if (isKit && componentSearch.length > 2) {
            const searchTermLower = componentSearch.toLowerCase();
            const results = allProducts.filter(p => 
                p.name.toLowerCase().includes(searchTermLower) && 
                !components.some(c => c.productId === p.id) &&
                p.type !== 'kit'
            );
            setSearchResults(results);
        } else {
            setSearchResults([]);
        }
    }, [componentSearch, isKit, allProducts, components]);

    const addComponent = () => {
        if (selectedComponent && componentQuantity > 0) {
            setComponents(prev => [...prev, { productId: selectedComponent.id, name: selectedComponent.name, quantity: componentQuantity }]);
            setSelectedComponent(null);
            setComponentSearch('');
            setComponentQuantity(1);
            setSearchResults([]);
        }
    };

    const removeComponent = (index) => {
        setComponents(prev => prev.filter((_, i) => i !== index));
    };

    const kitTotalCost = useMemo(() => {
        if (!isKit) return 0;
        return components.reduce((total, comp) => {
            const componentData = allProducts.find(p => p.id === comp.productId);
            const componentCost = componentData ? componentData.cost : 0;
            return total + (componentCost * comp.quantity);
        }, 0);
    }, [components, allProducts, isKit]);

    const handleQuickSave = (newSimpleProduct) => {
        if (updateAllProducts) updateAllProducts(newSimpleProduct);
        setComponents(prev => [...prev, { productId: newSimpleProduct.id, name: newSimpleProduct.name, quantity: 1 }]);
        setComponentSearch('');
        setSearchResults([]);
        setShowQuickAddModal(false);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if(!file) return;
        try {
            const compressed = await imageCompression(file, { maxSizeMB: 0.5, maxWidthOrHeight: 800 });
            setImageFile(compressed);
            setImagePreview(URL.createObjectURL(compressed));
        } catch(err) { console.error(err); }
    };

    const handlePdfUpload = (e) => {
        const file = e.target.files[0];
        if(file) setPdfFile(file);
    };

    const handleSave = async () => {
        if (!name.trim()) { setNotification({ message: 'El nombre es obligatorio.', type: 'error' }); return; }
        if (isKit && components.length === 0) { setNotification({ message: 'Un kit debe tener componentes.', type: 'error' }); return; }

        setLoading(true);
        try {
            const productData = {
                id: product?.id,
                name: name.trim(),
                description: description.trim(),
                type: isKit ? 'kit' : 'simple',
                imageUrl, 
                pdfUrl,
                ...(isKit ? {
                    components: components,
                    cost: kitTotalCost,
                    unit: kitUnit
                } : {
                    unit: unit,
                    cost: parseFloat(cost) || 0,
                    stock: parseInt(stock) || 0, 
                })
            };

            const savedProduct = await productService.saveProduct({
                productData,
                imageFile,
                pdfFile,
                isEditMode: !!product
            });

            onSave(savedProduct);
            setNotification({ message: product ? 'Actualizado.' : 'Creado.', type: 'success' });
            onClose();
        } catch (error) {
            console.error(error);
            setNotification({ message: 'Error al guardar.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
             {showQuickAddModal && (
                <QuickAddSimpleModal
                    initialName={componentSearch}
                    onClose={() => setShowQuickAddModal(false)}
                    onSave={handleQuickSave}
                    setNotification={setNotification}
                    systemParameters={systemParameters}
                />
            )}

            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto transition-colors">
                <div className="flex justify-between items-center mb-6 border-b dark:border-gray-700 pb-3 transition-colors">
                    <h2 className="text-2xl font-bold dark:text-gray-100 transition-colors">{product ? (isKit ? 'Editar Kit' : 'Editar Producto') : (isKit ? 'Nuevo Kit' : 'Nuevo Producto')}</h2>
                    <button onClick={onClose}><XCircleIcon className="w-7 h-7 text-gray-500 hover:text-gray-800 dark:hover:text-gray-300 transition-colors" /></button>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Nombre *</label>
                            <input type="text" value={name} onChange={e => setName(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Descripción</label>
                            <textarea value={description} onChange={e => setDescription(e.target.value)} rows="2" className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors"></textarea>
                        </div>

                        {isKit ? (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Unidad del Kit</label>
                                    <select value={kitUnit} onChange={e => setKitUnit(e.target.value)} className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors">
                                        <option value="Kit">Kit</option>
                                        {availableUnits.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                </div>
                                <div className="border-t dark:border-gray-700 pt-4 mt-4 space-y-4 bg-purple-50 dark:bg-purple-900/10 p-4 rounded-lg transition-colors">
                                    <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-300 transition-colors">Componentes del Kit</h3>
                                    <div className="relative">
                                        <input 
                                            type="text" 
                                            value={componentSearch} 
                                            onChange={e => { setComponentSearch(e.target.value); setSelectedComponent(null); }} 
                                            placeholder="Buscar producto para añadir..." 
                                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors" 
                                        />
                                        {(searchResults.length > 0 || (componentSearch.length > 2 && searchResults.length === 0)) && (
                                            <ul className="absolute z-10 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md mt-1 max-h-40 overflow-y-auto shadow-lg transition-colors">
                                                {searchResults.length > 0 ? searchResults.map(p => (
                                                    <li key={p.id} onClick={() => { setSelectedComponent(p); setComponentSearch(p.name); setSearchResults([]); }} className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-200 cursor-pointer text-sm transition-colors">
                                                        {p.name} ({formatCurrency(p.cost)})
                                                    </li>
                                                )) : (
                                                    <li onClick={() => setShowQuickAddModal(true)} className="px-4 py-2 hover:bg-green-50 dark:hover:bg-green-900/30 cursor-pointer text-sm text-green-700 dark:text-green-400 font-semibold transition-colors">
                                                        + Crear nuevo: "{componentSearch}"
                                                    </li>
                                                )}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="flex gap-2 items-end">
                                        <div className="flex-1"><label className="text-xs dark:text-gray-300 transition-colors">Cantidad</label><input type="number" value={componentQuantity} onChange={e => setComponentQuantity(parseInt(e.target.value)||1)} min="1" className="w-full rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"/></div>
                                        <button type="button" onClick={addComponent} disabled={!selectedComponent} className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:bg-gray-300 transition-colors">Añadir</button>
                                    </div>
                                    <div className="space-y-2">
                                        {components.map((comp, idx) => (
                                            <div key={idx} className="flex justify-between items-center bg-white dark:bg-gray-700 p-2 rounded shadow-sm border dark:border-gray-600 transition-colors">
                                                <span className="text-sm dark:text-gray-100 transition-colors">{comp.name} x {comp.quantity}</span>
                                                <button type="button" onClick={() => removeComponent(idx)} className="text-red-500 hover:text-red-700 dark:hover:text-red-400"><TrashIcon className="w-4 h-4"/></button>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-right font-bold text-purple-900 dark:text-purple-300 mt-2 transition-colors">Costo Total: {formatCurrency(kitTotalCost)}</p>
                                </div>
                            </>
                        ) : (
                            <div className="grid grid-cols-2 gap-4 border dark:border-gray-700 p-4 rounded-lg bg-gray-50 dark:bg-gray-900/50 transition-colors">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Unidad</label>
                                    <select value={unit} onChange={e => setUnit(e.target.value)} className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors">
                                        {availableUnits.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Costo Unitario</label>
                                    <input type="number" value={cost} onChange={e => setCost(parseFloat(e.target.value) || 0)} className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm font-bold transition-colors"/>
                                </div>
                                
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 transition-colors">
                                        <ArchiveBoxIcon className="w-4 h-4 text-blue-600 dark:text-blue-400"/> Stock Actual
                                    </label>
                                    <input 
                                        type="number" 
                                        value={stock} 
                                        onChange={e => setStock(parseInt(e.target.value) || 0)} 
                                        className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm font-bold text-blue-800 dark:text-blue-300 transition-colors"
                                        placeholder="Cantidad disponible"
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 transition-colors">Inventario disponible en bodega.</p>
                                </div>
                                
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Imagen</label>
                                    <div className="flex items-center gap-4">
                                        {imagePreview ? <img src={imagePreview} alt="preview" className="h-20 w-20 object-cover rounded border dark:border-gray-600"/> : <PhotoIcon className="h-20 w-20 text-gray-300 dark:text-gray-600"/>}
                                        <div>
                                            <label className="cursor-pointer bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-2 rounded text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors">
                                                Subir Foto <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                            </label>
                                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 transition-colors">O pegar enlace Drive:</div>
                                            <input type="text" value={imageUrl} onChange={e => { setImageUrl(e.target.value); setImagePreview(getDirectUrl(e.target.value)); }} className="text-xs w-full border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded mt-1 transition-colors" placeholder="https://drive.google.com..." />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Ficha Técnica (PDF)</label>
                                    <div className="flex items-center gap-4">
                                        <DocumentTextIcon className={`h-10 w-10 ${pdfUrl || pdfFile ? 'text-red-500 dark:text-red-400' : 'text-gray-300 dark:text-gray-600'} transition-colors`}/>
                                        <div>
                                            <label className="cursor-pointer bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-3 py-2 rounded text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                                Subir PDF <input type="file" className="hidden" accept="application/pdf" onChange={handlePdfUpload} />
                                            </label>
                                            {pdfFile && <span className="text-xs text-green-600 block mt-1">Archivo seleccionado</span>}
                                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 transition-colors">O pegar enlace:</div>
                                            <input type="text" value={pdfUrl} onChange={e => setPdfUrl(e.target.value)} className="text-xs w-full border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded mt-1 transition-colors" placeholder="URL del PDF..." />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end space-x-4 pt-6 border-t dark:border-gray-700 mt-6 transition-colors">
                            <button type="button" onClick={onClose} className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg transition-colors">Cancelar</button>
                            <button type="submit" disabled={loading} className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:bg-blue-300 dark:disabled:bg-blue-900/50 transition-colors">
                                {loading ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};


const ProductsPage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [activeTab, setActiveTab] = useState('simple');
    const [previewImage, setPreviewImage] = useState(null);
    const [systemParameters, setSystemParameters] = useState(null);
    const fileInputRef = useRef(null);

    const userPermissions = user?.permissions || {};

    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await productService.getInitialData();
            setSystemParameters(data.parameters);
            setProducts(data.products);
        } catch (error) {
            setNotification({ message: "Error al cargar productos.", type: "error" });
        } finally {
            setLoading(false);
        }
    }, [setNotification]);

    useEffect(() => {
        if (user) fetchInitialData();
    }, [user, fetchInitialData]);

    const filteredProducts = useMemo(() => {
        if (!user) return [];
        return products.filter(p => {
            const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
            const isKitTab = activeTab === 'kits';
            const isKitType = p.type === 'kit';
            return matchesSearch && (isKitTab ? isKitType : !isKitType);
        });
    }, [products, searchTerm, activeTab, user]);

    if (!user) return null;

    const isSuperUser = user.role === 'superuser';
    const isAdmin = user.role === 'admin' || isSuperUser;

    const canEdit = isAdmin || userPermissions.products?.edit;
    const canCreate = isAdmin || userPermissions.products?.create;
    const canDelete = isAdmin || userPermissions.products?.delete;

    const updateAllProducts = (newProduct) => {
        setProducts(prev => [newProduct, ...prev].sort((a, b) => a.name.localeCompare(b.name)));
    };

    const handleSaveProduct = (savedProduct) => {
        if (editingProduct) setProducts(prev => prev.map(p => p.id === savedProduct.id ? savedProduct : p));
        else updateAllProducts(savedProduct);
        setShowModal(false);
        setEditingProduct(null);
    };

    const handleDelete = async (e, product) => {
        e.stopPropagation();
        if (!window.confirm(`¿Eliminar "${product.name}"?`)) return;
        try {
            await productService.deleteProduct(product);
            setProducts(prev => prev.filter(p => p.id !== product.id));
            setNotification({ message: "Eliminado.", type: "success" });
        } catch (error) {
            setNotification({ message: "Error al eliminar.", type: "error" });
        }
    };

    const handleExportToExcel = () => {
        const productsToExport = products.filter(p => {
            if (activeTab === 'simple') {
                return p.type === 'simple' || p.type === 'servicio' || !p.type;
            } else {
                return p.type === 'kit';
            }
        });

        if (productsToExport.length === 0) return setNotification({ message: `No hay ${activeTab === 'simple' ? 'productos' : 'kits'} para exportar.`, type: "info" });
        
        let csv = "data:text/csv;charset=utf-8,Nombre;Tipo;Unidad;Costo;Stock;Descripcion;Imagen;PDF\n";
        productsToExport.forEach(p => {
            csv += [p.name, p.type, p.unit, p.cost, p.stock || 0, (p.description||'').replace(/;/g,','), p.imageUrl||'', p.pdfUrl||''].join(";") + "\n";
        });
        
        const universalBOM = "\uFEFF";
        const blob = new Blob([universalBOM + csv.substring(csv.indexOf(',')+1)], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        const filename = activeTab === 'simple' ? 'productos_simples' : 'kits_paquetes';
        link.download = `${filename}_${new Date().toISOString().slice(0,10)}.csv`;
        link.click();
    };

    const handleImportClick = () => fileInputRef.current.click();
    
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const defaultType = activeTab === 'kits' ? 'kit' : 'simple';
                const count = await productService.importFromCSV(evt.target.result, defaultType);
                
                if(count > 0) {
                    await fetchInitialData(); 
                    setNotification({ message: `Importados ${count} items.`, type: 'success' });
                } else {
                    setNotification({ message: "No se encontraron items válidos.", type: "info" });
                }
            } catch(err) { setNotification({ message: "Error al importar.", type: 'error' }); }
        };
        reader.readAsText(file, 'UTF-8');
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors">
            {previewImage && (
                <div className="fixed inset-0 bg-black bg-opacity-80 z-[70] flex items-center justify-center p-4 transition-colors" onClick={() => setPreviewImage(null)}>
                    <div className="relative max-w-4xl max-h-full">
                        <button onClick={() => setPreviewImage(null)} className="absolute -top-10 right-0 text-white"><XCircleIcon className="w-8 h-8" /></button>
                        <img src={getDirectUrl(previewImage)} alt="Full Preview" className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain" />
                    </div>
                </div>
            )}

            {showModal && (
                <ProductFormModal 
                    product={editingProduct} 
                    isKit={activeTab === 'kits'} 
                    onClose={() => { setShowModal(false); setEditingProduct(null); }} 
                    onSave={handleSaveProduct} 
                    allProducts={products.filter(p => p.type !== 'kit')} 
                    setNotification={setNotification} 
                    systemParameters={systemParameters} 
                    updateAllProducts={updateAllProducts}
                />
            )}

            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div className="flex items-center">
                    <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold mr-4 transition-colors">&larr; Volver</button>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Catálogo e Inventario</h1>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    {canCreate && (
                        <>
                            <button onClick={() => { setEditingProduct(null); setShowModal(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center shadow-md whitespace-nowrap">
                                <PlusCircleIcon className="w-5 h-5 mr-1"/> Nuevo {activeTab === 'kits' ? 'Kit' : 'Item'}
                            </button>
                            <button onClick={handleImportClick} className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition flex items-center shadow-md" title="Importar CSV"><DocumentArrowDownIcon className="w-5 h-5 mr-1"/> Importar</button>
                            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv" className="hidden" />
                        </>
                    )}
                    <button onClick={handleExportToExcel} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center shadow-md"><DocumentArrowDownIcon className="w-5 h-5 mr-1"/> Excel</button>
                </div>
            </div>

            <div className="flex mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-1 w-full md:w-fit transition-colors">
                <button onClick={() => setActiveTab('simple')} className={`flex-1 md:flex-none px-6 py-2 rounded-md text-sm font-semibold transition flex items-center justify-center ${activeTab === 'simple' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                    <CubeIcon className="w-4 h-4 mr-2"/> Productos y Servicios
                </button>
                <button onClick={() => setActiveTab('kits')} className={`flex-1 md:flex-none px-6 py-2 rounded-md text-sm font-semibold transition flex items-center justify-center ${activeTab === 'kits' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                    <Square3Stack3DIcon className="w-4 h-4 mr-2"/> Kits y Paquetes
                </button>
            </div>

            <div className="mb-6">
                <input type="text" placeholder={`Buscar en ${activeTab === 'simple' ? 'Productos' : 'Kits'}...`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full md:w-1/3 px-4 py-2 border dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none shadow-sm transition-colors" />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-x-auto transition-colors">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                    <thead className="bg-gray-100 dark:bg-gray-700/50 transition-colors">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors">Imagen</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors">Nombre</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors">Tipo</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors">Descripción</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors">Unidad</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors">Costo</th>
                            {activeTab === 'simple' && (
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors">Stock</th>
                            )}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors">Docs</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase transition-colors">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                        {loading ? (
                            <tr><td colSpan="9" className="text-center py-10 dark:text-gray-400 transition-colors">Cargando...</td></tr>
                        ) : (
                            filteredProducts.map(product => (
                                <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {product.imageUrl ? (
                                            <button onClick={() => setPreviewImage(product.imageUrl)} className="focus:outline-none">
                                                <img src={getDirectUrl(product.imageUrl)} alt="thumb" className="h-10 w-10 rounded object-cover border dark:border-gray-600 transition-colors" />
                                            </button>
                                        ) : (
                                            <div className="h-10 w-10 rounded bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center text-gray-400 dark:text-gray-500 transition-colors">
                                                <PhotoIcon className="w-6 h-6"/>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-800 dark:text-gray-100 transition-colors">{product.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full transition-colors ${
                                            product.type === 'kit' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                                        }`}>
                                            {product.type === 'kit' ? 'Kit' : 'Simple'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate transition-colors">{product.description}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 transition-colors">{product.unit}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 text-right font-mono transition-colors">
                                        {formatCurrency(product.cost)}
                                    </td>
                                    {/* COLUMNA DE STOCK */}
                                    {activeTab === 'simple' && (
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                                            <span className={`font-bold px-2 py-1 rounded transition-colors ${
                                                (product.stock || 0) <= 5 
                                                ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20' 
                                                : 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
                                            }`}>
                                                {product.stock || 0}
                                            </span>
                                        </td>
                                    )}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {product.pdfUrl && (
                                            <a href={getDirectUrl(product.pdfUrl)} target="_blank" rel="noreferrer" className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors" title="Ver PDF">
                                                <DocumentTextIcon className="w-5 h-5"/>
                                            </a>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                                        {canEdit && (
                                            <button onClick={() => { setEditingProduct(product); setShowModal(true); }} className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors" title="Editar">
                                                <PencilSquareIcon className="w-5 h-5"/>
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button onClick={(e) => handleDelete(e, product)} className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors" title="Eliminar">
                                                <TrashIcon className="w-5 h-5"/>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                { !loading && filteredProducts.length === 0 && <p className="text-center py-10 text-gray-500 dark:text-gray-400 transition-colors">No se encontraron items.</p>}
            </div>
        </div>
    );
};

export default ProductsPage;